import { useEffect, useState } from "react";
import { Check, ArrowLeft, ArrowRight, Building2, MessageCircle, Mail, Phone, Sparkles, ExternalLink, Loader2, X, CheckCircle2, AlertCircle, Eye, EyeOff } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { cn } from "../lib/utils";
import { toast } from "sonner";
import { createCompany, updateCompany, verifyIntegrations } from "../lib/api";
import { INVOICE_PROVIDERS } from "../lib/invoiceProviders";

const CHANNELS = [
  {
    key: "whatsapp",
    label: "וואטסאפ",
    icon: MessageCircle,
    color: "from-green-50 to-white border-green-300",
    activeColor: "from-green-100 to-green-50 border-green-500 text-green-900",
    helper: {
      title: "חיבור WhatsApp דרך Brevo",
      url: "https://app.brevo.com",
      text: "אותו מפתח Brevo שמשמש ל-Email גם שולח הודעות WhatsApp. צריך לחבר Meta Business + מספר WhatsApp דרך Brevo Console (Apps & Integrations → WhatsApp). אישור Meta נדרש (1-3 ימי עסקים).",
    },
  },
  {
    key: "email",
    label: "אימייל",
    icon: Mail,
    color: "from-blue-50 to-white border-blue-300",
    activeColor: "from-blue-100 to-blue-50 border-blue-500 text-blue-900",
    helper: {
      title: "חיבור שליחת אימייל דרך Brevo",
      url: "https://app.brevo.com/settings/keys/api",
      text: "1. נרשמים ב-brevo.com (חינם). 2. נכנסים ל-SMTP & API ← טאב API Keys ← Generate a new API key. 3. מעתיקים את המפתח (מתחיל ב-xkeysib_). 4. אין חובה לאמת דומיין — אפשר לשלוח מיד מכתובת שהרשמתי איתה.",
    },
  },
  {
    key: "sms",
    label: "SMS",
    icon: Phone,
    color: "from-orange-50 to-white border-orange-300",
    activeColor: "from-orange-100 to-orange-50 border-orange-500 text-orange-900",
    helper: {
      title: "חיבור SMS — בקרוב",
      url: "https://hub.360dialog.com/",
      text: "כרגע SMS אינו פעיל במערכת. עדיף להשתמש בערוץ WhatsApp (דרך 360dialog) או Email (Brevo) להעברת המסמכים.",
    },
  },
];

const EMPTY = {
  name: "",
  slug: "",           // URL-friendly unique identifier (e.g. "menshealth" → /menshealth)
  display_name: "",   // Name shown to recipient in email "From" and prefix of WhatsApp message
  logo_url: "",       // Optional company logo (data URL or hosted URL)
  contact_name: "",
  phone: "",
  email: "",
  channels: [],
  // Email provider selection
  email_provider: "brevo",  // "brevo" (default, free) or "resend"
  brevo_api_key: "",
  brevo_from_email: "",
  resend_api_key: "",
  resend_from_email: "",
  twilio_account_sid: "",
  twilio_auth_token: "",
  twilio_whatsapp_from: "",
  twilio_sms_from: "",
  // 360dialog (preferred WhatsApp provider — replaces Twilio)
  dialog360_api_key: "",
  dialog360_phone_number: "",
  // ManyChat (alternative — uses an existing ManyChat account
  // with WhatsApp Business already connected)
  manychat_api_key: "",
  //
  invoice_provider: "",
  invoice_provider_custom: "",
  invoice_api_key: "",
  invoice_api_secret: "",
  gsheet_url: "",
  notes: "",
};

// Fields that affect each integration — if any changes, its verification is invalidated.
const VERIFY_FIELDS = {
  email: ["email_provider", "brevo_api_key", "brevo_from_email", "resend_api_key", "resend_from_email"],
  whatsapp: ["brevo_api_key", "brevo_whatsapp_from"],
  invoice: ["invoice_provider", "invoice_api_key", "invoice_api_secret"],
  gsheet: ["gsheet_url"],
};

// Build the payload for /companies/verify-integrations.
// If `only` is provided, sends only that integration's data.
function buildVerifyPayload(form, only = null) {
  const all = {
    email_provider: form.email_provider || "brevo",
    brevo_api_key: form.channels.includes("email") ? form.brevo_api_key : "",
    brevo_from_email: form.brevo_from_email,
    resend_api_key: "",
    resend_from_email: "",
    twilio_account_sid: form.channels.includes("whatsapp") ? form.twilio_account_sid : "",
    twilio_auth_token: form.channels.includes("whatsapp") ? form.twilio_auth_token : "",
    twilio_whatsapp_from: form.channels.includes("whatsapp") ? form.twilio_whatsapp_from : "",
    dialog360_api_key: "",
    dialog360_phone_number: "",
    manychat_api_key: "",
    invoice_provider: form.invoice_provider,
    invoice_api_key: form.invoice_api_key,
    invoice_api_secret: form.invoice_api_secret,
    gsheet_url: form.gsheet_url,
  };
  if (!only) return all;
  // For per-step verify — send everything but mask out unrelated integrations
  // so backend returns null for them (not a red error).
  const blank = {
    email_provider: "brevo",
    brevo_api_key: "",
    brevo_from_email: "",
    resend_api_key: "",
    resend_from_email: "",
    twilio_account_sid: "",
    twilio_auth_token: "",
    dialog360_api_key: "",
    dialog360_phone_number: "",
    manychat_api_key: "",
    invoice_provider: "",
    invoice_api_key: "",
    invoice_api_secret: "",
    gsheet_url: "",
  };
  VERIFY_FIELDS[only]?.forEach((f) => { blank[f] = all[f]; });
  return blank;
}

export function CompanyWizard({ open, onOpenChange, editing, onSaved }) {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [verifyingOne, setVerifyingOne] = useState(null); // "email" | "whatsapp" | "invoice" | "gsheet"
  // Password/token visibility (each field independently)
  const [show, setShow] = useState({
    twilio_auth_token: false,
    dialog360_api_key: false,
    manychat_api_key: false,
    brevo_api_key: false,
    invoice_api_key: false,
    invoice_api_secret: false,
  });
  // per-integration: null = not checked, {ok, detail} = result
  const [verifyResults, setVerifyResults] = useState({
    email: null, whatsapp: null, invoice: null, gsheet: null,
  });

  useEffect(() => {
    if (open) {
      setStep(1);
      setForm(editing ? { ...EMPTY, ...editing } : EMPTY);
      setVerifyResults({ email: null, whatsapp: null, invoice: null, gsheet: null });
    }
  }, [open, editing]);

  // Helper: update form field + invalidate verification of any integration that uses it.
  const setField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setVerifyResults((prev) => {
      const next = { ...prev };
      for (const [intg, fields] of Object.entries(VERIFY_FIELDS)) {
        if (fields.includes(key)) next[intg] = null;
      }
      return next;
    });
  };

  const runVerify = async () => {
    setVerifying(true);
    try {
      const payload = buildVerifyPayload(form);
      const { integrations } = await verifyIntegrations(payload);
      setVerifyResults({
        email: integrations.email,
        whatsapp: integrations.whatsapp,
        invoice: integrations.invoice,
        gsheet: integrations.gsheet,
      });
      const failed = Object.values(integrations).filter((v) => v && v.ok === false);
      if (failed.length === 0) {
        toast.success("נתונים עלו בהצלחה ✓");
      } else {
        toast.error(`${failed.length} חיבורים נכשלו - בדקי את הפרטים`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "שגיאה באימות החיבורים");
    } finally {
      setVerifying(false);
    }
  };

  // Verify just ONE integration (per-step verify buttons)
  const verifyOne = async (key) => {
    setVerifyingOne(key);
    try {
      // Build a payload with ONLY the relevant fields for this integration,
      // so unrelated missing fields don't hide the result.
      const payload = buildVerifyPayload(form, key);
      const { integrations } = await verifyIntegrations(payload);
      const result = integrations[key];
      setVerifyResults((prev) => ({ ...prev, [key]: result }));
      if (result?.ok) toast.success("✓ נתונים עלו בהצלחה");
      else if (result) toast.error(result.detail || "החיבור נכשל");
      else toast.error("השלימי את הפרטים לפני האימות");
    } catch (e) {
      toast.error(e.response?.data?.detail || "שגיאה באימות");
    } finally {
      setVerifyingOne(null);
    }
  };

  // Integrations currently configured in the form (i.e. have enough info to verify)
  const configuredIntegrations = () => {
    const out = [];
    if (form.channels.includes("email")) {
      if (form.email_provider === "brevo" && form.brevo_api_key) out.push("email");
      else if (form.email_provider === "resend" && form.resend_api_key) out.push("email");
    }
    if ((form.channels.includes("whatsapp") || form.channels.includes("sms")) && form.twilio_account_sid && form.twilio_auth_token) out.push("whatsapp");
    if (form.invoice_provider === "green_invoice" && form.invoice_api_key && form.invoice_api_secret) out.push("invoice");
    if (form.gsheet_url) out.push("gsheet");
    return out;
  };

  // All configured integrations must be ok=true before saving
  const allVerified = () => {
    const configured = configuredIntegrations();
    return configured.every((k) => verifyResults[k]?.ok === true);
  };
  const hasAnyFailed = () => Object.values(verifyResults).some((v) => v && v.ok === false);

  const selectedProvider = INVOICE_PROVIDERS.find((p) => p.key === form.invoice_provider);

  const toggleChannel = (key) => {
    const has = form.channels.includes(key);
    setForm({ ...form, channels: has ? form.channels.filter((c) => c !== key) : [...form.channels, key] });
  };

  const canProceed = () => {
    if (step === 1) return !!form.name.trim();
    if (step === 2) return form.channels.length > 0;
    return true;
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      toast.error("שם חברה חובה");
      setStep(1);
      return;
    }
    // Block save if any integration was verified and failed
    if (hasAnyFailed()) {
      toast.error("לא ניתן לשמור — יש חיבורים שנכשלו. תקני את המפתחות ואמתי שוב.");
      return;
    }
    setSaving(true);
    try {
      if (editing?.id) {
        await updateCompany(editing.id, form);
        toast.success(`החברה "${form.name}" עודכנה`);
      } else {
        await createCompany(form);
        toast.success(`החברה "${form.name}" הוקמה בהצלחה!`);
      }
      onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "שגיאה בשמירה");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent dir="rtl" className="sm:max-w-xl max-h-[92vh] overflow-y-auto" data-testid="company-wizard">
        <DialogHeader>
          <DialogTitle className="text-right flex items-center gap-2">
            <Building2 className="h-5 w-5 text-brand" />
            {editing ? "עריכת חברה" : "הקמת חברה חדשה"}
          </DialogTitle>
          <DialogDescription className="text-right">
            {editing ? "עריכת פרטי החברה" : "פרטים בסיסיים — שאר ההגדרות נמצאות בכלי האדמין של הלקוח"}
          </DialogDescription>
        </DialogHeader>

        {/* Stepper hidden — single-step wizard (channels/keys live in
            the client's admin sidebar after creation) */}

        {/* STEP 1: Basic info */}
        {step === 1 && (
          <div className="space-y-3 py-2" data-testid="step-1">
            <h3 className="font-heading font-bold text-base">פרטי החברה</h3>

            {/* Logo upload zone — first thing the user sees */}
            <div className="rounded-xl border-2 border-dashed border-stone-300 bg-stone-50/60 p-4 text-center hover:border-brand transition-colors">
              <Label className="block mb-2 font-semibold">🖼️ לוגו החברה (אופציונלי)</Label>
              {form.logo_url ? (
                <div className="flex items-center justify-center gap-3">
                  <img src={form.logo_url} alt="logo" className="h-16 w-16 rounded-lg border border-stone-200 object-contain bg-white" />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setForm({ ...form, logo_url: "" })}
                    data-testid="remove-logo-btn"
                  >
                    הסירי לוגו
                  </Button>
                </div>
              ) : (
                <label className="cursor-pointer inline-flex flex-col items-center gap-2">
                  <div className="h-16 w-16 rounded-lg bg-white border border-stone-200 flex items-center justify-center text-stone-400">
                    <span className="text-2xl">+</span>
                  </div>
                  <span className="text-xs text-stone-600">לחצי כדי לבחור קובץ (PNG/JPG עד 2MB)</span>
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    data-testid="logo-upload-input"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      if (file.size > 2 * 1024 * 1024) {
                        alert("הקובץ גדול מדי — מקסימום 2MB");
                        return;
                      }
                      const reader = new FileReader();
                      reader.onload = (ev) => setForm({ ...form, logo_url: String(ev.target?.result || "") });
                      reader.readAsDataURL(file);
                    }}
                  />
                </label>
              )}
            </div>

            <div>
              <Label>שם החברה *</Label>
              <Input
                value={form.name}
                onChange={(e) => {
                  const newName = e.target.value;
                  // Auto-fill slug from name if user hasn't customised it
                  setForm((f) => ({
                    ...f,
                    name: newName,
                    slug: f.slug || newName.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, ""),
                  }));
                }}
                placeholder="ת.ל הבית"
                className="mt-1"
                data-testid="company-name-input"
                autoFocus
              />
            </div>

            {/* === SLUG (customer's dashboard URL) === */}
            <div className="bg-fuchsia-50 border border-fuchsia-200 rounded-lg p-3 space-y-1.5">
              <Label className="font-bold text-fuchsia-900">🔗 כתובת ייחודית לדשבורד של הלקוח *</Label>
              <div className="flex items-center gap-1 mt-1">
                <span className="text-xs text-stone-500 font-mono shrink-0">flowup.co.il/</span>
                <Input
                  dir="ltr"
                  value={form.slug}
                  onChange={(e) => setForm({ ...form, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "") })}
                  placeholder="menshealth"
                  className="text-right font-mono text-sm bg-white"
                  data-testid="company-slug-input"
                />
              </div>
              <p className="text-[11px] text-fuchsia-900 leading-relaxed">
                אנגלית בלבד · אותיות קטנות · מקפים. זו הכתובת שהלקוח יקבל לדשבורד האישי שלו.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 space-y-1">
              <Label className="font-bold text-amber-900">✉️ איך תרצי שיראו את השם שלך בהודעות ללקוח?</Label>
              <Input
                value={form.display_name}
                onChange={(e) => setForm({ ...form, display_name: e.target.value })}
                placeholder={`דוגמה: ${form.name || "שם העסק שלך"}`}
                className="mt-1"
                data-testid="company-display-name-input"
              />
              <p className="text-[11px] text-amber-800 leading-relaxed">
                זהו השם שיוצג במייל בתור השולח (לא הכתובת), ו/או בראש ההודעה בוואטסאפ. אם תשאירי ריק — נציג את שם החברה.
              </p>
            </div>

            {/* Section divider — clarifies the contact fields below are
                internal/registration only, not customer-facing. */}
            <div className="rounded-lg bg-blue-50 border border-blue-200 p-2.5 mt-2">
              <p className="text-xs text-blue-900 leading-relaxed">
                ℹ️ <strong>פרטים אלו לרישום בלבד</strong> — הם לא יוצגו ללקוחות שלך, רק לטובת הקשר איתך מצידנו אם נצטרך.
              </p>
            </div>

            <div>
              <Label>שם איש קשר</Label>
              <Input
                value={form.contact_name}
                onChange={(e) => setForm({ ...form, contact_name: e.target.value })}
                placeholder="מנכ״ל / בעל עסק / רו״ח"
                className="mt-1"
                data-testid="company-contact-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>אימייל</Label>
                <Input
                  type="email"
                  dir="ltr"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder="contact@company.co.il"
                  className="mt-1 text-right"
                  data-testid="company-email-input"
                />
              </div>
              <div>
                <Label>טלפון</Label>
                <Input
                  dir="ltr"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  placeholder="050-1234567"
                  className="mt-1 text-right"
                  data-testid="company-phone-input"
                />
              </div>
            </div>
          </div>
        )}

        {/* STEP 2: Channels */}
        {step === 2 && (
          <div className="space-y-3 py-2" data-testid="step-2">
            <div>
              <h3 className="font-heading font-bold text-base">ערוצי שליחת חשבוניות</h3>
              <p className="text-xs text-stone-500 mt-0.5">
                איך החברה רוצה לשלוח חשבוניות ללקוחות? ניתן לבחור יותר מאחד.
              </p>
            </div>
            <div className="grid gap-2">
              {CHANNELS.map((ch) => {
                const Icon = ch.icon;
                const active = form.channels.includes(ch.key);
                return (
                  <button
                    key={ch.key}
                    type="button"
                    onClick={(e) => { e.preventDefault(); toggleChannel(ch.key); }}
                    className={cn(
                      "w-full text-right p-4 rounded-xl border-2 bg-gradient-to-br transition-all flex items-center gap-3",
                      active ? ch.activeColor : ch.color + " hover:border-brand/30"
                    )}
                    data-testid={`channel-${ch.key}`}
                  >
                    <div className={cn(
                      "h-10 w-10 rounded-lg flex items-center justify-center flex-shrink-0",
                      active ? "bg-white/60" : "bg-white"
                    )}>
                      <Icon className="h-5 w-5" />
                    </div>
                    <div className="flex-1">
                      <p className="font-bold">{ch.label}</p>
                      <p className="text-[11px] opacity-70 leading-relaxed mt-0.5">{ch.helper.title}</p>
                    </div>
                    <div className={cn(
                      "h-6 w-6 rounded-full border-2 flex items-center justify-center flex-shrink-0",
                      active ? "bg-white border-current" : "border-stone-300"
                    )}>
                      {active && <Check className="h-4 w-4" />}
                    </div>
                  </button>
                );
              })}
            </div>

            {/* AI helper for selected channels */}
            {form.channels.length > 0 && (
              <div className="bg-gradient-to-br from-purple-50 to-blue-50 border border-purple-200 rounded-xl p-3 space-y-2" data-testid="ai-helper">
                <div className="flex items-center gap-2 text-purple-700 font-bold text-xs">
                  <Sparkles className="h-4 w-4" />
                  מה צריך להכין לכל ערוץ
                </div>
                {form.channels.map((k) => {
                  const ch = CHANNELS.find((c) => c.key === k);
                  if (!ch) return null;
                  return (
                    <div key={k} className="text-[11px] bg-white/60 rounded-lg p-2">
                      <p className="font-bold text-stone-800">{ch.helper.title}</p>
                      <p className="text-stone-600 leading-relaxed mt-0.5">{ch.helper.text}</p>
                      {ch.helper.url && (
                        <a
                          href={ch.helper.url}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-1 mt-1 text-brand hover:underline font-semibold"
                        >
                          <ExternalLink className="h-3 w-3" />
                          פתח דף חיבור
                        </a>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {/* === Credentials per selected channel === */}

            {/* EMAIL - Brevo only (Resend removed as per user request) */}
            {form.channels.includes("email") && (
              <div className="bg-blue-50/50 border-2 border-blue-200 rounded-xl p-3 space-y-3" data-testid="email-creds">
                <div className="flex items-center gap-2">
                  <Mail className="h-4 w-4 text-blue-600" />
                  <p className="font-bold text-blue-900 text-sm">ספק שליחת אימייל · Brevo</p>
                  <span className="text-[10px] bg-emerald-600 text-white px-1.5 py-0.5 rounded-full font-bold ml-auto">חינם · 9,000/חודש</span>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label className="text-xs">Brevo API Key</Label>
                    <a
                      href="https://app.brevo.com/settings/keys/api"
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1 bg-emerald-600 hover:bg-emerald-700 text-white text-[10px] font-bold px-2 py-0.5 rounded-md transition"
                      data-testid="brevo-keys-link"
                    >
                      <ExternalLink className="h-3 w-3" />
                      פתחי Brevo API Keys
                    </a>
                  </div>
                  <div className="relative mt-1">
                    <Input
                      dir="ltr"
                      type={show.brevo_api_key ? "text" : "password"}
                      value={form.brevo_api_key}
                      onChange={(e) => setField("brevo_api_key", e.target.value)}
                      placeholder="xkeysib-xxxxxxxxxxxxxxxxxxxxx"
                      className="text-right font-mono text-xs pl-9"
                      data-testid="brevo-api-key-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShow((s) => ({ ...s, brevo_api_key: !s.brevo_api_key }))}
                      className="absolute left-2 top-1/2 -translate-y-1/2 text-stone-500 hover:text-stone-800"
                      data-testid="toggle-brevo-key"
                      aria-label="הצג/הסתר"
                    >
                      {show.brevo_api_key ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                  <div>
                    <Label className="text-xs">כתובת שליחה (From)</Label>
                    <Input
                      dir="ltr"
                      value={form.brevo_from_email}
                      onChange={(e) => setField("brevo_from_email", e.target.value)}
                      placeholder="office@yourcompany.com"
                      className="mt-1 text-right"
                      data-testid="brevo-from-email-input"
                    />
                    <p className="text-[10px] text-stone-500 mt-1">יכולה להיות הכתובת הרשומה ב-Brevo - לא חובה דומיין מאומת.</p>
                  </div>
                </div>
                <VerifyStatus result={verifyResults.email} testId="verify-email-status" />
                <Button
                  onClick={() => verifyOne("email")}
                  disabled={verifyingOne === "email" || !form.brevo_api_key}
                  size="sm"
                  className="w-full bg-emerald-600 hover:bg-emerald-700 text-white h-8 text-xs"
                  data-testid="verify-email-btn"
                >
                  {verifyingOne === "email"
                    ? <><Loader2 className="h-3.5 w-3.5 animate-spin ml-1" />בודק...</>
                    : <>✓ אמת חיבור Brevo</>}
                </Button>
              </div>
            )}

            {/* WHATSAPP - Brevo (single API key powers Email + WhatsApp + SMS) */}
            {form.channels.includes("whatsapp") && (
              <div className="bg-fuchsia-50/50 border-2 border-fuchsia-200 rounded-xl p-3 space-y-2" data-testid="wa-creds">
                <div className="flex items-center gap-2">
                  <MessageCircle className="h-4 w-4 text-fuchsia-600" />
                  <p className="font-bold text-fuchsia-900 text-sm">חיבור WhatsApp Business · Brevo</p>
                </div>
                <p className="text-[11px] text-fuchsia-800 leading-relaxed">
                  ✨ אותו מפתח Brevo שהזנת ב-Email לשליחת WhatsApp.
                  אחרי שמירה, חברי את חשבון Meta + מספר WhatsApp דרך Brevo Console
                  (Apps & Integrations → WhatsApp).
                </p>
              </div>
            )}
          </div>
        )}

        {/* STEP 3: Invoice provider */}
        {step === 3 && (
          <div className="space-y-3 py-2" data-testid="step-3">
            <div>
              <h3 className="font-heading font-bold text-base">ספק חשבוניות</h3>
              <p className="text-xs text-stone-500 mt-0.5">
                איזה מערכת חשבוניות החברה משתמשת? בחרי מהרשימה.
              </p>
            </div>
            <div>
              <Label>בחרי ספק</Label>
              <p className="text-[11px] text-stone-500 mt-0.5 mb-2">
                בגרסה הנוכחית רק חשבונית ירוקה זמינה. שאר הספקים יתווספו בעדכונים הבאים.
              </p>
              <div className="space-y-1.5 mt-1" data-testid="invoice-provider-list">
                {INVOICE_PROVIDERS.map((p) => {
                  const enabled = p.key === "green_invoice";
                  const selected = form.invoice_provider === p.key;
                  return (
                    <button
                      key={p.key}
                      type="button"
                      disabled={!enabled}
                      onClick={() => enabled && setField("invoice_provider", p.key)}
                      data-testid={`invoice-provider-${p.key}`}
                      className={[
                        "w-full text-right p-3 rounded-lg border-2 flex items-center gap-3 transition-all",
                        !enabled && "opacity-40 cursor-not-allowed bg-stone-50 border-stone-200",
                        enabled && !selected && "bg-white border-stone-200 hover:border-brand/50 cursor-pointer",
                        enabled && selected && "bg-gradient-to-l from-brand/10 to-white border-brand shadow-sm cursor-pointer",
                      ].filter(Boolean).join(" ")}
                    >
                      <div className={[
                        "h-6 w-6 rounded-full border-2 flex items-center justify-center flex-shrink-0",
                        selected ? "bg-brand border-brand" : "border-stone-300",
                      ].join(" ")}>
                        {selected && <Check className="h-4 w-4 text-white" />}
                      </div>
                      <div className="flex-1">
                        <p className="font-bold text-sm">{p.label}</p>
                        {!enabled && <p className="text-[10px] text-stone-500 mt-0.5">בקרוב — לא ניתן לבחירה כעת</p>}
                        {enabled && <p className="text-[10px] text-emerald-700 mt-0.5">✓ זמין כעת</p>}
                      </div>
                      {!enabled && (
                        <span className="text-[9px] font-bold text-stone-500 bg-stone-200 px-2 py-0.5 rounded-full">
                          בקרוב
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>

            {selectedProvider && (
              <>
                {selectedProvider.key === "other" && (
                  <div>
                    <Label>שם הספק</Label>
                    <Input
                      value={form.invoice_provider_custom}
                      onChange={(e) => setForm({ ...form, invoice_provider_custom: e.target.value })}
                      placeholder="לדוגמה: Morning / MyDocs / ..."
                      className="mt-1"
                      data-testid="provider-custom-input"
                    />
                  </div>
                )}

                {/* AI helper - where to find API */}
                <div className="bg-gradient-to-br from-purple-50 to-blue-50 border border-purple-200 rounded-xl p-3" data-testid="provider-helper">
                  <div className="flex items-center gap-2 text-purple-700 font-bold text-xs mb-1.5">
                    <Sparkles className="h-4 w-4" />
                    איפה למצוא את ה-API?
                  </div>
                  <p className="text-[11px] text-stone-700 leading-relaxed">{selectedProvider.hint}</p>
                  {selectedProvider.url && (
                    <a
                      href={selectedProvider.url}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1 mt-2 text-brand hover:underline font-semibold text-xs"
                    >
                      <ExternalLink className="h-3 w-3" />
                      פתח את {selectedProvider.label}
                    </a>
                  )}
                </div>

                {/* API fields */}
                <div className="grid gap-3">
                  {selectedProvider.fields?.includes("key") && (
                    <div>
                      <Label>API Key / Key ID</Label>
                      <Input
                        dir="ltr"
                        value={form.invoice_api_key}
                        onChange={(e) => setField("invoice_api_key", e.target.value)}
                        placeholder="הדביקי את המפתח כאן"
                        className="mt-1 text-right font-mono text-xs"
                        data-testid="api-key-input"
                      />
                    </div>
                  )}
                  {selectedProvider.fields?.includes("secret") && (
                    <div>
                      <Label>API Secret</Label>
                      <div className="relative mt-1">
                        <Input
                          dir="ltr"
                          type={show.invoice_api_secret ? "text" : "password"}
                          value={form.invoice_api_secret}
                          onChange={(e) => setField("invoice_api_secret", e.target.value)}
                          placeholder="הדביקי את ה-secret כאן"
                          className="text-right font-mono text-xs pl-9"
                          data-testid="api-secret-input"
                        />
                        <button
                          type="button"
                          onClick={() => setShow((s) => ({ ...s, invoice_api_secret: !s.invoice_api_secret }))}
                          className="absolute left-2 top-1/2 -translate-y-1/2 text-stone-500 hover:text-stone-800"
                          data-testid="toggle-invoice-secret"
                          aria-label="הצג/הסתר"
                        >
                          {show.invoice_api_secret ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                      </div>
                    </div>
                  )}
                  {selectedProvider.fields?.includes("token") && (
                    <div>
                      <Label>API Token</Label>
                      <Input
                        dir="ltr"
                        value={form.invoice_api_key}
                        onChange={(e) => setField("invoice_api_key", e.target.value)}
                        placeholder="הדביקי את הטוקן כאן"
                        className="mt-1 text-right font-mono text-xs"
                        data-testid="api-token-input"
                      />
                    </div>
                  )}
                </div>
                <VerifyStatus result={verifyResults.invoice} testId="verify-invoice-status" />
                <Button
                  onClick={() => verifyOne("invoice")}
                  disabled={verifyingOne === "invoice" || !form.invoice_api_key || !form.invoice_api_secret}
                  size="sm"
                  className="w-full bg-brand hover:bg-brand-hover text-white h-8 text-xs"
                  data-testid="verify-invoice-btn"
                >
                  {verifyingOne === "invoice"
                    ? <><Loader2 className="h-3.5 w-3.5 animate-spin ml-1" />בודק...</>
                    : <>✓ אמת חיבור לספק חשבוניות</>}
                </Button>
              </>
            )}
          </div>
        )}

        {/* STEP 4: Data source */}
        {step === 4 && (
          <div className="space-y-3 py-2" data-testid="step-4">
            <div>
              <h3 className="font-heading font-bold text-base">מקור נתוני לקוחות</h3>
              <p className="text-xs text-stone-500 mt-0.5">
                מאיפה המערכת תשלוף את רשימת הלקוחות לשליחה.
              </p>
            </div>
            <div>
              <Label>קישור Google Sheets</Label>
              <Input
                dir="ltr"
                value={form.gsheet_url}
                onChange={(e) => setField("gsheet_url", e.target.value)}
                placeholder="https://docs.google.com/spreadsheets/d/..."
                className="mt-1 text-right"
                data-testid="gsheet-url-input"
              />
              <p className="text-[11px] text-stone-500 mt-1 leading-relaxed">
                💡 חשוב: הגיליון חייב להיות משותף "לכל מי שיש את הקישור" (גישת צופה). ניתן גם להשאיר ריק ולהעלות קובץ CSV/Excel בהמשך.
              </p>
              <VerifyStatus result={verifyResults.gsheet} testId="verify-gsheet-status" className="mt-2" />
              <Button
                onClick={() => verifyOne("gsheet")}
                disabled={verifyingOne === "gsheet" || !form.gsheet_url}
                size="sm"
                className="w-full mt-2 bg-orange-600 hover:bg-orange-700 text-white h-8 text-xs"
                data-testid="verify-gsheet-btn"
              >
                {verifyingOne === "gsheet"
                  ? <><Loader2 className="h-3.5 w-3.5 animate-spin ml-1" />בודק...</>
                  : <>✓ אמת קישור Google Sheets</>}
              </Button>
            </div>
            <div>
              <Label>הערות פנימיות</Label>
              <Textarea
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                placeholder="מידע נוסף על החברה שרק את רואה..."
                rows={3}
                className="mt-1"
                data-testid="company-notes-input"
              />
            </div>

            {/* Summary */}
            <div className="bg-brand-50/50 border border-brand/20 rounded-xl p-3 space-y-1 text-xs">
              <p className="font-bold text-brand-700 mb-1">📋 סיכום:</p>
              <p>חברה: <strong>{form.name || "—"}</strong></p>
              {form.contact_name && <p>איש קשר: {form.contact_name}</p>}
              {form.channels.length > 0 && (
                <p>ערוצים: {form.channels.map((c) => CHANNELS.find((x) => x.key === c)?.label).join(", ")}</p>
              )}
              {form.invoice_provider && (
                <p>ספק: {INVOICE_PROVIDERS.find((p) => p.key === form.invoice_provider)?.label}</p>
              )}
            </div>

            {/* === Live verification === */}
            <div className="bg-gradient-to-br from-emerald-50 to-teal-50 border-2 border-emerald-200 rounded-xl p-3 space-y-2" data-testid="verify-section">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Sparkles className="h-4 w-4 text-emerald-600" />
                  <p className="font-bold text-emerald-900 text-sm">בדיקת חיבורים חיה</p>
                </div>
                <Button
                  onClick={runVerify}
                  disabled={verifying || configuredIntegrations().length === 0}
                  size="sm"
                  className="bg-emerald-600 hover:bg-emerald-700 text-white h-8 text-xs"
                  data-testid="verify-integrations-button"
                >
                  {verifying ? (
                    <><Loader2 className="h-3.5 w-3.5 animate-spin ml-1" />בודק...</>
                  ) : (
                    <>✓ אמת חיבורים</>
                  )}
                </Button>
              </div>
              <p className="text-[11px] text-emerald-800 leading-relaxed">
                המערכת תבדוק בזמן אמת את כל המפתחות שהזנת (Brevo, Twilio, חשבונית ירוקה, Google Sheets) לפני שמירת החברה.
              </p>
              {configuredIntegrations().length === 0 && (
                <p className="text-[11px] text-amber-700 bg-amber-50 rounded-md p-2">
                  ℹ️ לא הוזנו מפתחות לאימות. אפשר לשמור את החברה ולהוסיף מפתחות בהמשך.
                </p>
              )}

              {/* Results grid */}
              {Object.values(verifyResults).some((v) => v !== null) && (
                <div className="grid gap-1.5 pt-1" data-testid="verify-results-grid">
                  <VerifyRow label="שליחת אימייל (Brevo)" result={verifyResults.email} />
                  <VerifyRow label="וואטסאפ (Twilio)" result={verifyResults.whatsapp} />
                  <VerifyRow label="ספק חשבוניות" result={verifyResults.invoice} />
                  <VerifyRow label="Google Sheets" result={verifyResults.gsheet} />
                </div>
              )}

              {allVerified() && configuredIntegrations().length > 0 && (
                <div className="bg-gradient-to-l from-blue-600 to-blue-700 text-white rounded-lg px-4 py-3 space-y-2 shadow-lg" data-testid="all-verified-banner">
                  <div className="flex items-center gap-2 font-bold text-sm">
                    <CheckCircle2 className="h-5 w-5" />
                    נתונים עלו בהצלחה ✓
                  </div>
                  <div className="grid grid-cols-2 gap-1.5 text-[11px]">
                    {verifyResults.email?.ok && (
                      <div className="flex items-center gap-1.5 bg-white/15 rounded px-2 py-1">
                        <CheckCircle2 className="h-3.5 w-3.5" /> אימייל (Brevo)
                      </div>
                    )}
                    {verifyResults.whatsapp?.ok && (
                      <div className="flex items-center gap-1.5 bg-white/15 rounded px-2 py-1">
                        <CheckCircle2 className="h-3.5 w-3.5" /> וואטסאפ (Twilio)
                      </div>
                    )}
                    {verifyResults.invoice?.ok && (
                      <div className="flex items-center gap-1.5 bg-white/15 rounded px-2 py-1">
                        <CheckCircle2 className="h-3.5 w-3.5" /> ספק חשבוניות
                      </div>
                    )}
                    {verifyResults.gsheet?.ok && (
                      <div className="flex items-center gap-1.5 bg-white/15 rounded px-2 py-1">
                        <CheckCircle2 className="h-3.5 w-3.5" /> Google Sheets
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        <DialogFooter className="flex-row-reverse gap-2 sm:gap-2 mt-4">
          {/* Always show the Save button — single-step wizard. */}
          <Button
            onClick={handleSave}
            disabled={saving || !form.name.trim() || hasAnyFailed()}
            className={cn(
              "text-white",
              hasAnyFailed() ? "bg-red-400 cursor-not-allowed" : "bg-brand hover:bg-brand-hover"
            )}
            data-testid="save-company-button"
            title={hasAnyFailed() ? "תקני את החיבורים שנכשלו לפני השמירה" : ""}
          >
            {saving ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <>
                <Check className="h-4 w-4 ml-2" />
                {editing ? "שמירת שינויים" : "הקמת חברה"}
              </>
            )}
          </Button>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            ביטול
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// --- Helper components for verification results ---

function VerifyStatus({ result, testId, className = "" }) {
  if (!result) return null;
  const Icon = result.ok ? CheckCircle2 : AlertCircle;
  return (
    <div
      className={cn(
        "flex items-start gap-2 text-[11px] font-semibold rounded-md px-2 py-1.5 border",
        result.ok
          ? "bg-green-50 text-green-800 border-green-300"
          : "bg-red-50 text-red-800 border-red-300",
        className
      )}
      data-testid={testId}
    >
      <Icon className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" />
      <span className="leading-snug">
        {result.ok ? "נתונים עלו בהצלחה — " : "שגיאה — "}
        <span className="font-normal">{result.detail}</span>
      </span>
    </div>
  );
}

function VerifyRow({ label, result }) {
  if (result === null) return null;
  const Icon = result.ok ? CheckCircle2 : AlertCircle;
  return (
    <div
      className={cn(
        "flex items-start gap-2 text-[11px] rounded-md px-2 py-1.5",
        result.ok ? "bg-green-50 text-green-800" : "bg-red-50 text-red-800"
      )}
    >
      <Icon className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" />
      <div className="flex-1">
        <p className="font-bold">{label}</p>
        <p className="text-[10px] opacity-80">{result.detail}</p>
      </div>
    </div>
  );
}
